<script setup>
import { computed, ref, watch } from 'vue';
import { debounce } from '@chatwoot/utils';
import { useI18n } from 'vue-i18n';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { useAlert } from 'dashboard/composables';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const props = defineProps({
  assistantId: {
    type: Number,
    required: true,
  },
});

const store = useStore();
const { t } = useI18n();
const { checkPermissions } = usePolicy();
const canManage = computed(() => checkPermissions(['administrator']));

const suggestions = useMapGetter('captainFaqLearning/getSuggestions');
const observations = useMapGetter('captainFaqLearning/getObservations');
const suggestionsMeta = useMapGetter('captainFaqLearning/getSuggestionsMeta');
const observationsMeta = useMapGetter('captainFaqLearning/getObservationsMeta');
const selectedSuggestion = useMapGetter(
  'captainFaqLearning/getSelectedSuggestion'
);
const uiFlags = useMapGetter('captainFaqLearning/getUIFlags');

const activeTab = ref('suggestions');
const searchQuery = ref('');
const statusFilter = ref('');
const reviewNote = ref('');
const confirmationDialog = ref(null);
const pendingConfirmation = ref(null);

const suggestionStatuses = [
  'open',
  'approved',
  'dismissed',
  'needs_clarification',
  'clarification_queued',
  'clarification_sent',
  'duplicate',
  'failed',
];
const observationStatuses = [
  'attached',
  'discarded',
  'pending',
  'processing',
  'clarification',
  'faq_created',
  'duplicate',
  'failed',
  'blacklist_imported',
  'blacklist_invalid',
];

const currentRecords = computed(() =>
  activeTab.value === 'suggestions' ? suggestions.value : observations.value
);
const currentMeta = computed(() =>
  activeTab.value === 'suggestions'
    ? suggestionsMeta.value
    : observationsMeta.value
);
const currentStatuses = computed(() =>
  activeTab.value === 'suggestions' ? suggestionStatuses : observationStatuses
);
const isLoading = computed(() =>
  activeTab.value === 'suggestions'
    ? uiFlags.value.fetchingSuggestions
    : uiFlags.value.fetchingObservations
);
const totalPages = computed(() => Number(currentMeta.value.total_pages) || 0);
const currentPage = computed(() => Number(currentMeta.value.page) || 1);
const confirmationDescription = computed(() => {
  const action = pendingConfirmation.value?.translationAction;
  if (action === 'REPROCESS') {
    return t('CAPTAIN.RESPONSES.FAQ_LEARNING.REPROCESS_CONFIRM');
  }
  if (action === 'DISCARD') {
    return t('CAPTAIN.RESPONSES.FAQ_LEARNING.DISCARD_CONFIRM');
  }
  if (action === 'OBSERVATION_REPROCESS') {
    return t('CAPTAIN.RESPONSES.FAQ_LEARNING.OBSERVATION_REPROCESS_CONFIRM');
  }
  if (action === 'OBSERVATION_DISCARD') {
    return t('CAPTAIN.RESPONSES.FAQ_LEARNING.OBSERVATION_DISCARD_CONFIRM');
  }
  return '';
});
const confirmationButtonLabel = computed(() => {
  const pending = pendingConfirmation.value;
  if (!pending) return '';
  if (pending.kind === 'suggestion') {
    if (pending.action === 'review') {
      return t('CAPTAIN.RESPONSES.FAQ_LEARNING.REVIEW');
    }
    if (pending.action === 'reprocess') {
      return t('CAPTAIN.RESPONSES.FAQ_LEARNING.REPROCESS');
    }
    return t('CAPTAIN.RESPONSES.FAQ_LEARNING.DISCARD');
  }
  if (pending.action === 'reprocessObservation') {
    return t('CAPTAIN.RESPONSES.FAQ_LEARNING.OBSERVATION_REPROCESS');
  }
  return t('CAPTAIN.RESPONSES.FAQ_LEARNING.OBSERVATION_DISCARD');
});

const STATUS_LABELS = {
  open: () => t('CAPTAIN.RESPONSES.FAQ_LEARNING.STATUS.OPEN'),
  approved: () => t('CAPTAIN.RESPONSES.FAQ_LEARNING.STATUS.APPROVED'),
  dismissed: () => t('CAPTAIN.RESPONSES.FAQ_LEARNING.STATUS.DISMISSED'),
  needs_clarification: () =>
    t('CAPTAIN.RESPONSES.FAQ_LEARNING.STATUS.NEEDS_CLARIFICATION'),
  clarification_queued: () =>
    t('CAPTAIN.RESPONSES.FAQ_LEARNING.STATUS.CLARIFICATION_QUEUED'),
  clarification_sent: () =>
    t('CAPTAIN.RESPONSES.FAQ_LEARNING.STATUS.CLARIFICATION_SENT'),
  attached: () => t('CAPTAIN.RESPONSES.FAQ_LEARNING.STATUS.ATTACHED'),
  discarded: () => t('CAPTAIN.RESPONSES.FAQ_LEARNING.STATUS.DISCARDED'),
  pending: () => t('CAPTAIN.RESPONSES.FAQ_LEARNING.STATUS.PENDING'),
  processing: () => t('CAPTAIN.RESPONSES.FAQ_LEARNING.STATUS.PROCESSING'),
  clarification: () => t('CAPTAIN.RESPONSES.FAQ_LEARNING.STATUS.CLARIFICATION'),
  faq_created: () => t('CAPTAIN.RESPONSES.FAQ_LEARNING.STATUS.FAQ_CREATED'),
  duplicate: () => t('CAPTAIN.RESPONSES.FAQ_LEARNING.STATUS.DUPLICATE'),
  failed: () => t('CAPTAIN.RESPONSES.FAQ_LEARNING.STATUS.FAILED'),
  blacklist_imported: () =>
    t('CAPTAIN.RESPONSES.FAQ_LEARNING.STATUS.BLACKLIST_IMPORTED'),
  blacklist_invalid: () =>
    t('CAPTAIN.RESPONSES.FAQ_LEARNING.STATUS.BLACKLIST_INVALID'),
};

const statusLabel = status => {
  const normalizedStatus = status.toString().toLowerCase();
  const translateStatus = STATUS_LABELS[normalizedStatus];
  return translateStatus
    ? translateStatus()
    : normalizedStatus.replaceAll('_', ' ');
};

const suggestionActionSuccessMessage = action => {
  if (action === 'review') {
    return t('CAPTAIN.RESPONSES.FAQ_LEARNING.REVIEW_SUCCESS');
  }
  if (action === 'reprocess') {
    return t('CAPTAIN.RESPONSES.FAQ_LEARNING.REPROCESS_SUCCESS');
  }
  return t('CAPTAIN.RESPONSES.FAQ_LEARNING.DISCARD_SUCCESS');
};

const observationActionSuccessMessage = action =>
  action === 'reprocessObservation'
    ? t('CAPTAIN.RESPONSES.FAQ_LEARNING.OBSERVATION_REPROCESS_SUCCESS')
    : t('CAPTAIN.RESPONSES.FAQ_LEARNING.OBSERVATION_DISCARD_SUCCESS');

const formatDate = value => {
  if (!value) return t('CAPTAIN.RESPONSES.FAQ_LEARNING.NOT_AVAILABLE');
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value));
};

const formatConfidence = value => {
  if (value === null || value === undefined) {
    return t('CAPTAIN.RESPONSES.FAQ_LEARNING.NOT_AVAILABLE');
  }
  return `${Math.round(Number(value) * 100)}%`;
};

const formatEvidence = evidence => JSON.stringify(evidence || {}, null, 2);

const fetchList = (page = 1) => {
  if (!props.assistantId) return Promise.resolve();

  const params = {
    assistantId: props.assistantId,
    page,
    status: statusFilter.value || undefined,
    search: searchQuery.value || undefined,
  };
  const action =
    activeTab.value === 'suggestions'
      ? 'captainFaqLearning/fetchSuggestions'
      : 'captainFaqLearning/fetchObservations';
  return store.dispatch(action, params).catch(error => {
    useAlert(error?.message || t('CAPTAIN.RESPONSES.FAQ_LEARNING.LOAD_ERROR'));
  });
};

const debouncedFetch = debounce(() => fetchList(1), 400);

const changeTab = tab => {
  if (activeTab.value === tab) return;
  activeTab.value = tab;
  searchQuery.value = '';
  statusFilter.value = '';
  reviewNote.value = '';
  store.dispatch('captainFaqLearning/clearSelectedSuggestion');
  fetchList(1);
};

const openSuggestion = suggestionId => {
  if (selectedSuggestion.value?.id === suggestionId) {
    reviewNote.value = '';
    return store.dispatch('captainFaqLearning/clearSelectedSuggestion');
  }

  reviewNote.value = '';
  return store
    .dispatch('captainFaqLearning/fetchSuggestion', {
      assistantId: props.assistantId,
      suggestionId,
    })
    .catch(error => {
      useAlert(
        error?.message || t('CAPTAIN.RESPONSES.FAQ_LEARNING.LOAD_ERROR')
      );
    });
};

const changeDetailPage = page => {
  if (!selectedSuggestion.value?.id) return;
  store
    .dispatch('captainFaqLearning/fetchSuggestion', {
      assistantId: props.assistantId,
      suggestionId: selectedSuggestion.value.id,
      observationsPage: page,
    })
    .catch(error => useAlert(error?.message));
};

const refreshAfterAction = async suggestionId => {
  await fetchList(currentPage.value);
  await store.dispatch('captainFaqLearning/fetchSuggestion', {
    assistantId: props.assistantId,
    suggestionId,
  });
};

const performAction = async action => {
  const suggestionId = selectedSuggestion.value?.id;
  if (!suggestionId || !canManage.value) return;

  try {
    await store.dispatch(`captainFaqLearning/${action}`, {
      assistantId: props.assistantId,
      suggestionId,
      note: reviewNote.value,
    });
    useAlert(suggestionActionSuccessMessage(action));
    reviewNote.value = '';
    await refreshAfterAction(suggestionId);
  } catch (error) {
    useAlert(
      error?.message || t('CAPTAIN.RESPONSES.FAQ_LEARNING.ACTION_ERROR')
    );
  }
};

const performObservationAction = async (action, observationId) => {
  if (!observationId || !canManage.value) return;

  try {
    await store.dispatch(`captainFaqLearning/${action}`, {
      assistantId: props.assistantId,
      observationId,
    });
    useAlert(observationActionSuccessMessage(action));
    await fetchList(currentPage.value);
  } catch (error) {
    useAlert(
      error?.message || t('CAPTAIN.RESPONSES.FAQ_LEARNING.ACTION_ERROR')
    );
  }
};

const requestSuggestionAction = action => {
  if (action === 'review') {
    performAction(action);
    return;
  }
  pendingConfirmation.value = {
    kind: 'suggestion',
    action,
    translationAction: action.toUpperCase(),
  };
  confirmationDialog.value?.open();
};

const requestObservationAction = (action, observationId) => {
  pendingConfirmation.value = {
    kind: 'observation',
    action,
    observationId,
    translationAction:
      action === 'reprocessObservation'
        ? 'OBSERVATION_REPROCESS'
        : 'OBSERVATION_DISCARD',
  };
  confirmationDialog.value?.open();
};

const confirmPendingAction = async () => {
  const pending = pendingConfirmation.value;
  if (!pending) return;

  if (pending.kind === 'suggestion') {
    await performAction(pending.action);
  } else {
    await performObservationAction(pending.action, pending.observationId);
  }
  confirmationDialog.value?.close();
};

const clearPendingConfirmation = () => {
  pendingConfirmation.value = null;
};

watch(
  () => props.assistantId,
  () => {
    activeTab.value = 'suggestions';
    searchQuery.value = '';
    statusFilter.value = '';
    reviewNote.value = '';
    store.dispatch('captainFaqLearning/reset');
    fetchList(1);
  },
  { immediate: true }
);
</script>

<template>
  <section
    class="mb-5 overflow-hidden border rounded-xl border-n-weak bg-n-alpha-1"
    data-testid="faq-learning-panel"
  >
    <div class="flex flex-col gap-3 px-4 py-4 border-b border-n-weak">
      <div
        class="flex flex-col justify-between gap-3 md:flex-row md:items-start"
      >
        <div>
          <h2 class="mb-1 text-base font-medium text-n-slate-12">
            {{ $t('CAPTAIN.RESPONSES.FAQ_LEARNING.TITLE') }}
          </h2>
          <p class="m-0 text-sm text-n-slate-11">
            {{ $t('CAPTAIN.RESPONSES.FAQ_LEARNING.DESCRIPTION') }}
          </p>
        </div>
        <div class="inline-flex gap-1 p-1 rounded-lg bg-n-alpha-2">
          <Button
            size="xs"
            color="slate"
            :variant="activeTab === 'suggestions' ? 'faded' : 'ghost'"
            :label="$t('CAPTAIN.RESPONSES.FAQ_LEARNING.SUGGESTIONS')"
            @click="changeTab('suggestions')"
          />
          <Button
            size="xs"
            color="slate"
            :variant="activeTab === 'observations' ? 'faded' : 'ghost'"
            :label="$t('CAPTAIN.RESPONSES.FAQ_LEARNING.OBSERVATIONS')"
            @click="changeTab('observations')"
          />
        </div>
      </div>

      <div class="flex flex-col gap-2 sm:flex-row">
        <Input
          v-model="searchQuery"
          class="flex-1"
          size="sm"
          type="search"
          :placeholder="$t('CAPTAIN.RESPONSES.FAQ_LEARNING.SEARCH')"
          @input="debouncedFetch"
        />
        <select
          v-model="statusFilter"
          class="h-8 px-3 text-sm border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-12"
          :aria-label="$t('CAPTAIN.RESPONSES.FAQ_LEARNING.FILTER_STATUS')"
          @change="fetchList(1)"
        >
          <option value="">
            {{ $t('CAPTAIN.RESPONSES.FAQ_LEARNING.ALL_STATUSES') }}
          </option>
          <option
            v-for="status in currentStatuses"
            :key="status"
            :value="status"
          >
            {{ statusLabel(status) }}
          </option>
        </select>
      </div>
    </div>

    <div v-if="isLoading" class="flex items-center justify-center py-10">
      <Spinner />
    </div>

    <div
      v-else-if="currentRecords.length === 0"
      class="px-4 py-8 text-sm text-center text-n-slate-11"
    >
      {{ $t('CAPTAIN.RESPONSES.FAQ_LEARNING.EMPTY') }}
    </div>

    <div v-else-if="activeTab === 'suggestions'" class="divide-y divide-n-weak">
      <article
        v-for="suggestionItem in suggestions"
        :key="suggestionItem.id"
        class="p-4"
      >
        <div class="flex flex-col gap-2">
          <div class="flex items-start justify-between gap-3">
            <h3
              class="m-0 text-sm font-medium whitespace-pre-wrap break-words text-n-slate-12"
            >
              {{ suggestionItem.question }}
            </h3>
            <span
              class="shrink-0 px-2 py-0.5 text-xs rounded-full bg-n-alpha-2 text-n-slate-11"
            >
              {{ statusLabel(suggestionItem.status) }}
            </span>
          </div>
          <p
            class="m-0 text-sm whitespace-pre-wrap break-words text-n-slate-11"
          >
            {{ suggestionItem.answer }}
          </p>
          <div
            class="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-n-slate-10"
          >
            <span>
              {{
                $t('CAPTAIN.RESPONSES.FAQ_LEARNING.LABELED_VALUE', {
                  label: $t('CAPTAIN.RESPONSES.FAQ_LEARNING.CONFIDENCE'),
                  value: formatConfidence(suggestionItem.confidence),
                })
              }}
            </span>
            <span>
              {{
                $t('CAPTAIN.RESPONSES.FAQ_LEARNING.LABELED_VALUE', {
                  label: $t('CAPTAIN.RESPONSES.FAQ_LEARNING.SOURCES'),
                  value: suggestionItem.source_count,
                })
              }}
            </span>
            <span>{{ formatDate(suggestionItem.updated_at) }}</span>
          </div>
          <Button
            size="xs"
            variant="link"
            color="slate"
            class="self-start"
            :label="
              selectedSuggestion?.id === suggestionItem.id
                ? $t('CAPTAIN.RESPONSES.FAQ_LEARNING.HIDE_DETAILS')
                : $t('CAPTAIN.RESPONSES.FAQ_LEARNING.SHOW_DETAILS')
            "
            @click="openSuggestion(suggestionItem.id)"
          />
        </div>

        <div
          v-if="selectedSuggestion?.id === suggestionItem.id"
          class="flex flex-col gap-4 p-4 mt-3 border rounded-lg border-n-weak bg-n-alpha-1"
          data-testid="faq-learning-detail"
        >
          <div v-if="uiFlags.fetchingDetail" class="flex justify-center py-5">
            <Spinner />
          </div>
          <template v-else>
            <div class="grid gap-3 sm:grid-cols-2">
              <div>
                <h4 class="mb-1 text-xs font-medium uppercase text-n-slate-10">
                  {{ $t('CAPTAIN.RESPONSES.FAQ_LEARNING.GAPS') }}
                </h4>
                <p
                  class="m-0 text-sm whitespace-pre-wrap break-words text-n-slate-11"
                >
                  {{
                    selectedSuggestion.gaps?.missing_information?.join('\n') ||
                    $t('CAPTAIN.RESPONSES.FAQ_LEARNING.NONE')
                  }}
                </p>
              </div>
              <div>
                <h4 class="mb-1 text-xs font-medium uppercase text-n-slate-10">
                  {{ $t('CAPTAIN.RESPONSES.FAQ_LEARNING.CONFLICTS') }}
                </h4>
                <p
                  class="m-0 text-sm whitespace-pre-wrap break-words text-n-slate-11"
                >
                  {{
                    selectedSuggestion.gaps?.conflicts?.join('\n') ||
                    $t('CAPTAIN.RESPONSES.FAQ_LEARNING.NONE')
                  }}
                </p>
              </div>
            </div>

            <div v-if="selectedSuggestion.clarification?.question">
              <h4 class="mb-1 text-xs font-medium uppercase text-n-slate-10">
                {{ $t('CAPTAIN.RESPONSES.FAQ_LEARNING.CLARIFICATION') }}
              </h4>
              <p
                class="m-0 text-sm whitespace-pre-wrap break-words text-n-slate-11"
              >
                {{ selectedSuggestion.clarification.question }}
              </p>
              <div class="flex flex-wrap gap-3 mt-1 text-xs text-n-slate-10">
                <span v-if="selectedSuggestion.clarification.sent_at">
                  {{
                    $t('CAPTAIN.RESPONSES.FAQ_LEARNING.LABELED_VALUE', {
                      label: $t('CAPTAIN.RESPONSES.FAQ_LEARNING.SENT_AT'),
                      value: formatDate(
                        selectedSuggestion.clarification.sent_at
                      ),
                    })
                  }}
                </span>
                <span v-if="selectedSuggestion.clarification.answered_at">
                  {{
                    $t('CAPTAIN.RESPONSES.FAQ_LEARNING.LABELED_VALUE', {
                      label: $t('CAPTAIN.RESPONSES.FAQ_LEARNING.ANSWERED_AT'),
                      value: formatDate(
                        selectedSuggestion.clarification.answered_at
                      ),
                    })
                  }}
                </span>
              </div>
            </div>

            <div v-if="selectedSuggestion.error" class="text-sm text-n-ruby-9">
              <strong>
                {{
                  $t('CAPTAIN.RESPONSES.FAQ_LEARNING.LABEL_WITH_COLON', {
                    label: $t('CAPTAIN.RESPONSES.FAQ_LEARNING.ERROR'),
                  })
                }}
              </strong>
              <span class="whitespace-pre-wrap break-words">{{
                selectedSuggestion.error
              }}</span>
            </div>

            <div
              v-if="
                selectedSuggestion.duplicate?.suggestion_id ||
                selectedSuggestion.duplicate?.assistant_response_id
              "
              class="p-3 text-sm border rounded-lg border-n-weak text-n-slate-11"
            >
              <h4 class="mb-1 text-xs font-medium uppercase text-n-slate-10">
                {{ $t('CAPTAIN.RESPONSES.FAQ_LEARNING.DUPLICATE_DETAILS') }}
              </h4>
              <div class="flex flex-wrap gap-3">
                <span v-if="selectedSuggestion.duplicate.suggestion_id">
                  {{
                    $t('CAPTAIN.RESPONSES.FAQ_LEARNING.LABELED_VALUE', {
                      label: $t('CAPTAIN.RESPONSES.FAQ_LEARNING.SUGGESTION_ID'),
                      value: selectedSuggestion.duplicate.suggestion_id,
                    })
                  }}
                </span>
                <span v-if="selectedSuggestion.duplicate.assistant_response_id">
                  {{
                    $t('CAPTAIN.RESPONSES.FAQ_LEARNING.LABELED_VALUE', {
                      label: $t(
                        'CAPTAIN.RESPONSES.FAQ_LEARNING.ASSISTANT_RESPONSE_ID'
                      ),
                      value: selectedSuggestion.duplicate.assistant_response_id,
                    })
                  }}
                </span>
              </div>
            </div>

            <div
              v-if="
                selectedSuggestion.sources?.group_jids?.length ||
                selectedSuggestion.sources?.message_ids?.length
              "
            >
              <h4 class="mb-2 text-xs font-medium uppercase text-n-slate-10">
                {{ $t('CAPTAIN.RESPONSES.FAQ_LEARNING.SOURCE_REFERENCES') }}
              </h4>
              <pre
                class="max-w-full p-2 m-0 overflow-x-auto text-xs whitespace-pre-wrap break-all rounded bg-n-alpha-2 text-n-slate-11"
                :text-content.prop="formatEvidence(selectedSuggestion.sources)"
              />
            </div>

            <div
              v-if="selectedSuggestion.assistant_response"
              class="p-3 border rounded-lg border-n-weak"
            >
              <h4 class="mb-2 text-xs font-medium uppercase text-n-slate-10">
                {{
                  $t('CAPTAIN.RESPONSES.FAQ_LEARNING.LABELED_ID_WITH_STATUS', {
                    label: $t('CAPTAIN.RESPONSES.FAQ_LEARNING.LINKED_FAQ'),
                    id: selectedSuggestion.assistant_response.id,
                    status: statusLabel(
                      selectedSuggestion.assistant_response.status
                    ),
                  })
                }}
              </h4>
              <p
                class="mb-2 text-sm font-medium whitespace-pre-wrap break-words text-n-slate-12"
              >
                {{ selectedSuggestion.assistant_response.question }}
              </p>
              <p
                class="m-0 text-sm whitespace-pre-wrap break-words text-n-slate-11"
              >
                {{ selectedSuggestion.assistant_response.answer }}
              </p>
            </div>

            <div>
              <h4 class="mb-2 text-xs font-medium uppercase text-n-slate-10">
                {{ $t('CAPTAIN.RESPONSES.FAQ_LEARNING.EVIDENCE') }}
              </h4>
              <div
                v-if="selectedSuggestion.observations?.length"
                class="flex flex-col gap-3"
              >
                <div
                  v-for="observation in selectedSuggestion.observations"
                  :key="observation.id"
                  class="p-3 border rounded-lg border-n-weak"
                >
                  <div
                    class="flex flex-wrap justify-between gap-2 mb-2 text-xs text-n-slate-10"
                  >
                    <span>
                      {{
                        observation.source_group_subject ||
                        observation.source_group_jid ||
                        $t('CAPTAIN.RESPONSES.FAQ_LEARNING.NOT_AVAILABLE')
                      }}
                    </span>
                    <span>
                      {{
                        $t('CAPTAIN.RESPONSES.FAQ_LEARNING.STATUS_WITH_DATE', {
                          status: statusLabel(observation.status),
                          date: formatDate(
                            observation.observed_at || observation.created_at
                          ),
                        })
                      }}
                    </span>
                  </div>
                  <p
                    class="mb-2 text-sm whitespace-pre-wrap break-words text-n-slate-11"
                  >
                    {{ observation.source_excerpt }}
                  </p>
                  <div
                    class="flex flex-wrap gap-x-3 gap-y-1 mb-2 text-xs text-n-slate-10"
                  >
                    <span
                      v-for="outcome in observation.candidate_outcomes || []"
                      :key="
                        outcome.candidate_entry_key || outcome.suggestion_id
                      "
                      data-testid="faq-observation-candidate-outcome"
                    >
                      {{
                        $t(
                          'CAPTAIN.RESPONSES.FAQ_LEARNING.LABELED_ID_WITH_STATUS',
                          {
                            label: $t(
                              'CAPTAIN.RESPONSES.FAQ_LEARNING.SUGGESTION_ID'
                            ),
                            id: outcome.suggestion_id,
                            status: statusLabel(outcome.status || 'pending'),
                          }
                        )
                      }}
                    </span>
                    <span v-if="observation.source_group_jid">
                      {{
                        $t('CAPTAIN.RESPONSES.FAQ_LEARNING.LABELED_VALUE', {
                          label: $t('CAPTAIN.RESPONSES.FAQ_LEARNING.GROUP_JID'),
                          value: observation.source_group_jid,
                        })
                      }}
                    </span>
                    <span v-if="observation.source_message_id">
                      {{
                        $t('CAPTAIN.RESPONSES.FAQ_LEARNING.LABELED_VALUE', {
                          label: $t(
                            'CAPTAIN.RESPONSES.FAQ_LEARNING.MESSAGE_ID'
                          ),
                          value: observation.source_message_id,
                        })
                      }}
                    </span>
                    <span v-if="observation.conversation_id">
                      {{
                        $t('CAPTAIN.RESPONSES.FAQ_LEARNING.LABELED_VALUE', {
                          label: $t(
                            'CAPTAIN.RESPONSES.FAQ_LEARNING.CONVERSATION_ID'
                          ),
                          value: observation.conversation_id,
                        })
                      }}
                    </span>
                    <span>
                      {{
                        $t('CAPTAIN.RESPONSES.FAQ_LEARNING.LABELED_VALUE', {
                          label: $t('CAPTAIN.RESPONSES.FAQ_LEARNING.ATTEMPTS'),
                          value: observation.processing_attempts || 0,
                        })
                      }}
                    </span>
                    <span v-if="observation.processing_started_at">
                      {{
                        $t('CAPTAIN.RESPONSES.FAQ_LEARNING.LABELED_VALUE', {
                          label: $t(
                            'CAPTAIN.RESPONSES.FAQ_LEARNING.PROCESSING_AT'
                          ),
                          value: formatDate(observation.processing_started_at),
                        })
                      }}
                    </span>
                  </div>
                  <pre
                    class="max-w-full p-2 m-0 overflow-x-auto text-xs whitespace-pre-wrap break-all rounded bg-n-alpha-2 text-n-slate-11"
                    :text-content.prop="formatEvidence(observation.evidence)"
                  />
                  <p
                    v-if="observation.error"
                    class="mt-2 mb-0 text-sm whitespace-pre-wrap break-words text-n-ruby-9"
                  >
                    {{ observation.error }}
                  </p>
                </div>
                <div
                  v-if="selectedSuggestion.observations_meta?.total_pages > 1"
                  class="flex items-center justify-between gap-3"
                >
                  <Button
                    size="xs"
                    variant="outline"
                    color="slate"
                    :disabled="
                      !selectedSuggestion.observations_meta.previous_page
                    "
                    :label="$t('CAPTAIN.RESPONSES.FAQ_LEARNING.PREVIOUS')"
                    @click="
                      changeDetailPage(
                        selectedSuggestion.observations_meta.previous_page
                      )
                    "
                  />
                  <span class="text-xs text-n-slate-10">
                    {{
                      $t('CAPTAIN.RESPONSES.FAQ_LEARNING.PAGE_FRACTION', {
                        page: selectedSuggestion.observations_meta.page,
                        pages: selectedSuggestion.observations_meta.total_pages,
                      })
                    }}
                  </span>
                  <Button
                    size="xs"
                    variant="outline"
                    color="slate"
                    :disabled="!selectedSuggestion.observations_meta.next_page"
                    :label="$t('CAPTAIN.RESPONSES.FAQ_LEARNING.NEXT')"
                    @click="
                      changeDetailPage(
                        selectedSuggestion.observations_meta.next_page
                      )
                    "
                  />
                </div>
              </div>
              <p v-else class="m-0 text-sm text-n-slate-11">
                {{ $t('CAPTAIN.RESPONSES.FAQ_LEARNING.NO_EVIDENCE') }}
              </p>
            </div>

            <div
              v-if="canManage"
              class="flex flex-col gap-2 pt-3 border-t border-n-weak"
            >
              <textarea
                v-model="reviewNote"
                rows="3"
                class="w-full px-3 py-2 text-sm border rounded-lg resize-y border-n-weak bg-n-alpha-1 text-n-slate-12"
                :placeholder="$t('CAPTAIN.RESPONSES.FAQ_LEARNING.REVIEW_NOTE')"
              />
              <div class="flex flex-wrap gap-2">
                <Button
                  v-if="selectedSuggestion.allowed_actions?.review"
                  size="sm"
                  variant="outline"
                  color="slate"
                  :is-loading="uiFlags.mutating"
                  :label="$t('CAPTAIN.RESPONSES.FAQ_LEARNING.REVIEW')"
                  @click="requestSuggestionAction('review')"
                />
                <Button
                  v-if="selectedSuggestion.allowed_actions?.reprocess"
                  size="sm"
                  variant="outline"
                  color="amber"
                  :is-loading="uiFlags.mutating"
                  :label="$t('CAPTAIN.RESPONSES.FAQ_LEARNING.REPROCESS')"
                  @click="requestSuggestionAction('reprocess')"
                />
                <Button
                  v-if="selectedSuggestion.allowed_actions?.discard"
                  size="sm"
                  variant="outline"
                  color="ruby"
                  :is-loading="uiFlags.mutating"
                  :label="$t('CAPTAIN.RESPONSES.FAQ_LEARNING.DISCARD')"
                  @click="requestSuggestionAction('discard')"
                />
              </div>
            </div>
          </template>
        </div>
      </article>
    </div>

    <div v-else class="divide-y divide-n-weak">
      <article
        v-for="observation in observations"
        :key="observation.id"
        class="p-4"
      >
        <div class="flex flex-wrap items-start justify-between gap-2 mb-2">
          <div>
            <h3 class="m-0 text-sm font-medium text-n-slate-12">
              {{
                observation.source_group_subject ||
                observation.source_group_jid ||
                $t('CAPTAIN.RESPONSES.FAQ_LEARNING.NOT_AVAILABLE')
              }}
            </h3>
            <span class="text-xs text-n-slate-10">
              {{
                formatDate(observation.observed_at || observation.created_at)
              }}
            </span>
          </div>
          <span
            class="px-2 py-0.5 text-xs rounded-full bg-n-alpha-2 text-n-slate-11"
          >
            {{ statusLabel(observation.status) }}
          </span>
        </div>
        <p
          v-if="observation.generated_question"
          class="mb-1 text-sm font-medium whitespace-pre-wrap break-words text-n-slate-12"
        >
          {{ observation.generated_question }}
        </p>
        <p
          v-if="observation.generated_answer"
          class="mb-2 text-sm whitespace-pre-wrap break-words text-n-slate-11"
        >
          {{ observation.generated_answer }}
        </p>
        <p class="mb-2 text-sm whitespace-pre-wrap break-words text-n-slate-11">
          {{ observation.source_excerpt }}
        </p>
        <div
          class="flex flex-wrap gap-x-3 gap-y-1 mb-2 text-xs text-n-slate-10"
        >
          <span
            v-for="outcome in observation.candidate_outcomes || []"
            :key="outcome.candidate_entry_key || outcome.suggestion_id"
            data-testid="faq-observation-candidate-outcome"
          >
            {{
              $t('CAPTAIN.RESPONSES.FAQ_LEARNING.LABELED_ID_WITH_STATUS', {
                label: $t('CAPTAIN.RESPONSES.FAQ_LEARNING.SUGGESTION_ID'),
                id: outcome.suggestion_id,
                status: statusLabel(outcome.status || 'pending'),
              })
            }}
          </span>
          <span v-if="observation.source_group_jid">
            {{
              $t('CAPTAIN.RESPONSES.FAQ_LEARNING.LABELED_VALUE', {
                label: $t('CAPTAIN.RESPONSES.FAQ_LEARNING.GROUP_JID'),
                value: observation.source_group_jid,
              })
            }}
          </span>
          <span v-if="observation.source_message_id">
            {{
              $t('CAPTAIN.RESPONSES.FAQ_LEARNING.LABELED_VALUE', {
                label: $t('CAPTAIN.RESPONSES.FAQ_LEARNING.MESSAGE_ID'),
                value: observation.source_message_id,
              })
            }}
          </span>
          <span v-if="observation.conversation_id">
            {{
              $t('CAPTAIN.RESPONSES.FAQ_LEARNING.LABELED_VALUE', {
                label: $t('CAPTAIN.RESPONSES.FAQ_LEARNING.CONVERSATION_ID'),
                value: observation.conversation_id,
              })
            }}
          </span>
          <span>
            {{
              $t('CAPTAIN.RESPONSES.FAQ_LEARNING.LABELED_VALUE', {
                label: $t('CAPTAIN.RESPONSES.FAQ_LEARNING.ATTEMPTS'),
                value: observation.processing_attempts || 0,
              })
            }}
          </span>
          <span v-if="observation.processing_started_at">
            {{
              $t('CAPTAIN.RESPONSES.FAQ_LEARNING.LABELED_VALUE', {
                label: $t('CAPTAIN.RESPONSES.FAQ_LEARNING.PROCESSING_AT'),
                value: formatDate(observation.processing_started_at),
              })
            }}
          </span>
        </div>
        <pre
          class="max-w-full p-2 m-0 overflow-x-auto text-xs whitespace-pre-wrap break-all rounded bg-n-alpha-2 text-n-slate-11"
          :text-content.prop="formatEvidence(observation.evidence)"
        />
        <p
          v-if="observation.error"
          class="mt-2 mb-0 text-sm whitespace-pre-wrap break-words text-n-ruby-9"
        >
          {{ observation.error }}
        </p>
        <div
          v-if="
            canManage &&
            (observation.allowed_actions?.reprocess ||
              observation.allowed_actions?.discard)
          "
          class="flex flex-wrap gap-2 mt-3"
        >
          <Button
            v-if="observation.allowed_actions?.reprocess"
            size="xs"
            variant="outline"
            color="amber"
            :is-loading="uiFlags.mutating"
            :label="$t('CAPTAIN.RESPONSES.FAQ_LEARNING.OBSERVATION_REPROCESS')"
            @click="
              requestObservationAction('reprocessObservation', observation.id)
            "
          />
          <Button
            v-if="observation.allowed_actions?.discard"
            size="xs"
            variant="outline"
            color="ruby"
            :is-loading="uiFlags.mutating"
            :label="$t('CAPTAIN.RESPONSES.FAQ_LEARNING.OBSERVATION_DISCARD')"
            @click="
              requestObservationAction('discardObservation', observation.id)
            "
          />
        </div>
      </article>
    </div>

    <div
      v-if="!isLoading && currentMeta.total_count > 0"
      class="flex items-center justify-between gap-3 px-4 py-3 border-t border-n-weak"
    >
      <Button
        size="xs"
        variant="outline"
        color="slate"
        :disabled="!currentMeta.previous_page"
        :label="$t('CAPTAIN.RESPONSES.FAQ_LEARNING.PREVIOUS')"
        @click="fetchList(currentMeta.previous_page)"
      />
      <span class="text-xs text-n-slate-10">
        {{
          $t('CAPTAIN.RESPONSES.FAQ_LEARNING.PAGE', {
            page: currentPage,
            pages: totalPages,
            count: currentMeta.total_count,
          })
        }}
      </span>
      <Button
        size="xs"
        variant="outline"
        color="slate"
        :disabled="!currentMeta.next_page"
        :label="$t('CAPTAIN.RESPONSES.FAQ_LEARNING.NEXT')"
        @click="fetchList(currentMeta.next_page)"
      />
    </div>

    <Dialog
      ref="confirmationDialog"
      type="alert"
      :title="$t('CAPTAIN.RESPONSES.FAQ_LEARNING.CONFIRM_TITLE')"
      :description="confirmationDescription"
      :confirm-button-label="confirmationButtonLabel"
      :is-loading="uiFlags.mutating"
      @confirm="confirmPendingAction"
      @close="clearPendingConfirmation"
    />
  </section>
</template>
